<template>
  <Modal
    ref="requestUpdateModalRef"
    :showIcon="false"
    :clickToClose="true"
    class="request-update-modal"
    :modalStyle="{
      width: '896px',
      height: '528px',
      padding: '24px',
    }"
    positiveActionText="Talep Oluştur"
    negativeActionText="Vazgeç"
    :isPositiveActionButtonDisabled="!isFormValid || isSubmitting"
    @onPositiveActionClick="handleSubmit"
    @onNegativeActionClick="handleClose"
    @modalClosed="handleModalClosed"
  >
    <template v-slot:body>
      <div class="header">
        <p class="title">
          Ürün Özellikleri için Güncelleme Talep Et/Hata Bildir
        </p>
      </div>

      <div ref="scrollableContentRef" class="scrollable-content">
        <div class="info-banner">
          <Icons name="InfoFilledIcon" size="md" color="#4FA4E5" />
          <span class="info-text">
            Mevcut ürün özelliklerini inceleyip, değiştirmek istediğiniz için
            öneri talep edebilirsiniz.
          </span>
        </div>

        <div class="form-container">
          <div class="two-column-grid">
            <div class="column">
              <h3 class="column-title">Mevcut Ürün Özellikleri</h3>
              <div class="fields-container">
                <div
                  v-for="(field, index) in currentProductFeatures"
                  :key="`current-${index}`"
                  class="field-group"
                >
                  <Input
                    type="text"
                    :value="field.value"
                    :label="field.label"
                    :placeholder="`${field.label} (Opsiyonel) (Henüz doldurmadınız.)`"
                    disabled
                    size="md"
                  />
                </div>
              </div>
            </div>

            <div class="column">
              <h3 class="column-title">Talep Ettiğiniz Ürün Özellikleri</h3>
              <div class="fields-container">
                <div
                  v-for="field in requestedProductFeatures"
                  :key="`requested-${field.key}`"
                  class="field-group"
                  :ref="(el) => setFieldGroupRef(field.key, el)"
                >
                  <DropdownSelectable
                    :dropItems="field.value"
                    :key="field.key"
                    :label="`${field.label} Seç`"
                    notFoundText="Değer bulunamadı."
                    isComboBox
                    customInputLabel="Yeni Değer Ekle"
                    customValuePlaceholder="Yeni Değer ekle"
                    size="md"
                    :singleItemTextLength="80"
                    :shouldShowEmptyIcon="true"
                    :isCustomValue="field.isCustomValue"
                    :searchText="`${field.label} Ara`"
                    @selectedRadioItem="(e) => selectRequestedAttributeValue(field.key, e)"
                    @changedDropdownState="(isOpen) => onDropdownState(isOpen, field.key)"
                  />
                </div>
              </div>

              <div class="form-group description-group">
                <div class="description-header">
                  <span class="description-title">Talep Açıklaması</span>
                  <span class="optional-badge">OPSİYONEL</span>
                </div>

                <Input
                  type="textarea"
                  v-model:value="description"
                  label="Talebinizi yazın"
                  :rows="4"
                  :maxlength="500"
                  size="md"
                />

                <div class="textarea-footer">
                  <span class="char-count">{{ description.length }}/500</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed, reactive, nextTick } from 'vue';
import { useStore } from 'vuex';
import { Modal, Input, Icons, DropdownSelectable } from '@n11/so-ui-components';
import { sendProductDetailEvent } from '@/utils/ga4/helper';
import { GA4_EVENT } from '@/utils/ga4/event';

const store = useStore();
const requestUpdateModalRef = ref(null);
const scrollableContentRef = ref(null);
const sourceSection = ref(null);
const description = ref('');
const isSubmitting = ref(false);
const skipDismissOnClose = ref(false);

const props = defineProps({
  productId: {
    type: [String, Number],
    required: true,
  },
});

const productDetail = computed(() => store.getters['products/getProductDetail']);
const groupId = computed(() => productDetail.value?.groupId || null);
const categoryId = computed(() => productDetail.value?.categoryId || null);

const categoryAttributes = computed(
  () => store.getters['categories/getCategoryAttributes'],
);

const currentProductFeatures = computed(() => {
  const attrs = productDetail.value?.attributes || [];
  if (!Array.isArray(attrs)) return [];

  return attrs.map((attr, index) => ({
    key: attr?.attributeId || `${index}`,
    label: attr?.attributeName || '',
    value: attr?.attributeValue || '',
  }));
});

const requestedProductFeatures = reactive([]);

const normalizeText = (val) => String(val || '').trim().toLowerCase();

const fieldGroupRefs = new Map();

const setFieldGroupRef = (fieldKey, el) => {
  if (el) fieldGroupRefs.set(fieldKey, el);
  else fieldGroupRefs.delete(fieldKey);
};

const onDropdownState = async (isOpen, fieldKey) => {
  if (!isOpen) return;

  await nextTick();

  const container = scrollableContentRef.value;
  const target = fieldGroupRefs.get(fieldKey);

  if (!container || !target) return;
  const containerRect = container.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();

  const currentScrollTop = container.scrollTop;
  const deltaTop = targetRect.top - containerRect.top;
  const paddingTop = 8;

  container.scrollTo({
    top: currentScrollTop + deltaTop - paddingTop,
    behavior: 'smooth',
  });
};

const buildRequestedProductFeatures = (attrs) => {
  const temp = (attrs || []).map((attr) => {
    const options =
      (attr?.attributeValues || []).map(({ id, value }) => ({
        id,
        name: value,
      })) || [];

    return {
      key: `attr-${attr?.attributeId || attr?.attributeName || ''}`,
      label: attr?.attributeName || '',
      value: options,
      selectedId: null,
      selectedName: '',
      attributeId: attr?.attributeId || null,
      isCustomValue: !!attr?.isCustomValue,
      searchable: !!attr?.searchable,
      categoryId: attr?.categoryId || null,
    };
  });

  const ordered = [];
  const remaining = [...temp];

  (currentProductFeatures.value || []).forEach((cur) => {
    const curKeyStr = `${cur?.key || ''}`;
    const curKeyNum =
      curKeyStr !== '' && !Number.isNaN(Number(curKeyStr)) ? Number(curKeyStr) : null;

    let idx = -1;

    if (curKeyNum !== null) {
      idx = remaining.findIndex((r) => Number(r?.attributeId) === curKeyNum);
    }

    if (idx === -1) {
      const curLabel = normalizeText(cur?.label || '');
      idx = remaining.findIndex((r) => normalizeText(r?.label || '') === curLabel);
    }

    if (idx > -1) {
      ordered.push(remaining[idx]);
      remaining.splice(idx, 1);
    }
  });

  ordered.push(...remaining);

  requestedProductFeatures.splice(0, requestedProductFeatures.length);
  ordered.forEach((item) => requestedProductFeatures.push(item));
};

const selectRequestedAttributeValue = (fieldKey, selected) => {
  const field = requestedProductFeatures.find((f) => f.key === fieldKey);
  if (!field) return;

  field.selectedId = selected?.id || null;
  field.selectedName = selected?.name || '';
};

const isFormValid = computed(() => {
  return requestedProductFeatures.some((f) => (f?.selectedName || '') !== '');
});

const handleSubmit = async () => {
  if (!isFormValid.value || isSubmitting.value) return;

  if (!groupId.value) {
    store.commit('common/SET_TOASTER_DATA', {
      type: 'error',
      title: 'Hata',
      description:
        'Ürün detayları alınamadı. Lütfen sayfayı yenileyip tekrar deneyin.',
    });
    return;
  }

  isSubmitting.value = true;

  try {
    const suggestedAttributes = requestedProductFeatures
      .filter((f) => (f?.selectedName || '') !== '')
      .map((f) => ({
        attributeName: f?.label || '',
        attributeValue: f?.selectedName || '',
      }));

    const payload = {
      groupId: groupId.value,
      suggestedAttributes,
      description: description.value || '',
    };

    await store.dispatch('products/submitAttributesFeedbackSuggestion', payload);
    sendProductDetailEvent(GA4_EVENT.REQUEST_UPDATE_REPORT_BUG_SUCCESS, {
      button_name: 'Talep Oluştur',
      button_type: 'Request Success',
      button_area: 'Product Specifications',
    });

    store.commit('common/SET_TOASTER_DATA', {
      type: 'success',
      title: 'Başarılı',
      description: 'Talep başarıyla oluşturuldu',
    });

    skipDismissOnClose.value = true;
    handleClose();
  } catch (error) {
    const errorMessage =
      error?.errors?.[0]?.message || 'Bir hata oluştu. Lütfen tekrar deneyin.';

    store.commit('common/SET_TOASTER_DATA', {
      type: 'error',
      title: 'Hata',
      description: errorMessage,
    });
  } finally {
    isSubmitting.value = false;
  }
};

const handleClose = () => {
  resetForm();
  requestUpdateModalRef.value?.hiddenModal();
};

const handleModalClosed = () => {
  if (!skipDismissOnClose.value) {
    sendProductDetailEvent(GA4_EVENT.REQUEST_UPDATE_REPORT_BUG_DISMISS, {
      button_name: 'Vazgeç',
      button_type: 'Request Dismiss',
      button_area: 'Product Specifications',
    });
  }
  skipDismissOnClose.value = false;
};

const resetForm = () => {
  sourceSection.value = null;
  description.value = '';
  isSubmitting.value = false;

  requestedProductFeatures.splice(0, requestedProductFeatures.length);
  fieldGroupRefs.clear();
  if (scrollableContentRef.value) {
    scrollableContentRef.value.scrollTop = 0;
  }
};

const openModal = async (source) => {
  resetForm();
  sourceSection.value = source;

  if (!groupId.value) {
    store.commit('common/SET_TOASTER_DATA', {
      type: 'error',
      title: 'Hata',
      description:
        'Ürün detayları henüz yüklenmedi. Lütfen sayfayı yenileyip tekrar deneyin.',
    });
    return;
  }

  if (!categoryId.value) {
    store.commit('common/SET_TOASTER_DATA', {
      type: 'error',
      title: 'Hata',
      description:
        'categoryId alınamadı. Lütfen sayfayı yenileyip tekrar deneyin.',
    });
    return;
  }

  try {
    const res = await store.dispatch(
      'categories/getCategoryAttributes',
      categoryId.value,
    );
    const attrs =
      res?.categoryAttributes ||
      categoryAttributes.value?.categoryAttributes ||
      [];
    buildRequestedProductFeatures(attrs);
  } catch (error) {
    buildRequestedProductFeatures([]);
  }

  requestUpdateModalRef.value?.openModal();
};

defineExpose({
  openModal,
});
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables.scss';

.request-update-modal {
  :deep(.modal-container) {
    display: flex;
    flex-direction: column;
    padding: 20px;
  }

  :deep(.modal-header) {
    position: absolute;
    top: 7px;
    right: 4px;
  }

  :deep(.modal-body) {
    padding: 0 !important;
    height: 100%;
    overflow-y: visible;

    .content {
      display: flex;
      flex-direction: column;
      flex: 1;
      overflow: hidden !important;
    }
  }

  :deep(.modal-footer) {
    padding: 8px 12px 0 0 !important;
  }
}

.header {
  margin-bottom: 12px;

  .title {
    color: $colorN80;
    font-size: 20px;
    font-weight: 700;
    line-height: 26px;
    letter-spacing: -0.2px;
    text-align: left;
  }
}

.scrollable-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: $colorN10;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: $colorN40;
    border-radius: 3px;

    &:hover {
      background: $colorN50;
    }
  }
}

.info-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background-color: $colorB05;
  border-radius: 6px;
  margin-bottom: 24px;

  .info-text {
    font-size: 13px;
    line-height: 18px;
    letter-spacing: -0.05px;
    color: $colorN80;
  }
}

.two-column-grid {
  display: flex;
  gap: 48px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 1px;
    background-color: $colorN20;
    transform: translateX(-50%);
  }

  .column {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;

    .column-title {
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      line-height: 20px;
      letter-spacing: -0.05px;
      color: $colorN80;
      margin-bottom: 4px;
      text-align: left;
    }

    .fields-container {
      display: flex;
      flex-direction: column;
      gap: 16px;

      :deep(.input-container) {
        margin-bottom: 0;
      }
    }

    &:nth-child(1) .fields-container {
      padding-top: 16px;
    }

    &:nth-child(2) .fields-container {
      background-color: #f5f3ef;
      border-radius: 8px;
      padding: 16px;
      z-index: 2;
    }
  }
}

.form-group.description-group {
  margin-top: 4px;

  :deep(.input-container) {
    margin-bottom: 0;
  }

  .description-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;

    .description-title {
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: 18px;
      letter-spacing: -0.05px;
      color: $colorN80;
    }

    .optional-badge {
      font-size: 10px;
      font-style: normal;
      font-weight: 700;
      line-height: normal;
      padding: 0 8px;
      color: $colorN60;
      border-radius: 3px;
      border: 0.5px solid $colorN30;
    }
  }

  .textarea-footer {
    display: flex;
    justify-content: flex-end;

    .char-count {
      font-size: 11px;
      margin-top: 4px;
      color: $colorN60;
    }
  }
}
</style>
